import { Chip } from '../../../components/ui/Chip'
import { EmptyState } from '../../../components/ui/EmptyState'
import { displayNameFor, findSourceableById } from '../../../lib/fixtures/sourceable-lookup'
import { PartThumbnail } from '../../rfq/PartThumbnail'
import { RFQ_STATUS_CHIP, formatQuantity } from '../../rfq/domain'
import type { Rfq, RfqLineView } from '../../rfq/domain'
import { formatDate } from '../format'

export interface WorkspaceRfqsTabProps {
  readonly items: readonly Rfq[]
  readonly onOpen: (id: string) => void
}

/**
 * The comp's Process column (`Project Workspace.dc.html:188/196`,
 * `r.process`) has no typed equivalent on the wire — `SourceableRef`/
 * `LineItem` carry neither a `process` field nor a name/SKU (those live on
 * the fuller fixture `Sourceable`, resolved via the SAME demo
 * `sourceable-lookup.ts` `RfqCard`/`RfqListRow` already use). Prefer the
 * fixture's display-ready `dfmSummary.suggestedProcess` (e.g.
 * `'CNC Milling'`/`'Injection Molding'`) — the same field DFM-summary UI
 * elsewhere in the app already shows to users — falling back to the raw
 * `configAttributes.process` slug (e.g. `'cnc'`/`'im'`) only when that's
 * absent, and `'—'` when neither is, same as every other optional cell in
 * this app (never a blank flash).
 */
function processLabelFor(line: RfqLineView): string {
  const sourceable = findSourceableById(line.sourceable.id)
  const suggested = sourceable?.dfmSummary?.suggestedProcess
  if (typeof suggested === 'string' && suggested.trim() !== '') {
    return suggested
  }
  const raw = sourceable?.configAttributes?.process
  return typeof raw === 'string' && raw.trim() !== '' ? raw : '—'
}

/**
 * `displayNameFor` (`sourceable-lookup.ts`) already appends
 * `(${catalogSku})` onto the name for catalog lines, so this cell would
 * just repeat the same SKU the name cell already shows; the two custom-line
 * fixtures (no `catalogSku`) have nothing to show here either way. Returns
 * `undefined` — never `'—'` — when the PN has nothing to add beyond what
 * `name` already displays, so the caller can drop the ` · pn` segment
 * entirely rather than render a redundant placeholder.
 */
function pnFor(line: RfqLineView, name: string): string | undefined {
  const sku = findSourceableById(line.sourceable.id)?.catalogSku
  return sku && !name.includes(sku) ? sku : undefined
}

/**
 * UID-300 — the Workspace detail page's RFQs tab
 * (`Project Workspace.dc.html:181-203`): a card-list of rows (thumbnail
 * placeholder / quote name+id·pn·qty / process / status / submitted date),
 * NOT the account-wide `/rfq` list's `RfqListRow` (bundled-badge disclosure,
 * quote-summary/ship-by, in-progress stepper, whole-row-link nav-restoration
 * plumbing) — the two screens render RFQs differently by design, so this is
 * a second, deliberately distinct presentational component, mirroring the
 * `WorkspaceBomsTab` precedent (UID-299) rather than forking `RfqListRow`.
 * Reuses `rfq/domain.ts`'s `RFQ_STATUS_CHIP`/`formatQuantity` and the shared
 * `sourceable-lookup.ts` name resolver so status color/label and part naming
 * stay in agreement with the account-wide RFQ screens.
 *
 * Only the FIRST line is rendered per row (comp fixtures are one-line-per-
 * RFQ) — a multi-line RFQ still renders correctly, just summarized by its
 * first line, same posture as `RfqCard`'s thumbnail (which is also
 * decorative-only/first-line-oriented) rather than growing a nested list
 * inside this already-compact row.
 */
export function WorkspaceRfqsTab({ items, onOpen }: WorkspaceRfqsTabProps) {
  if (items.length === 0) {
    return <EmptyState title="No RFQs in this workspace yet." />
  }

  return (
    <div className="workspace-rfqs-tab">
      <div className="workspace-rfqs-header" aria-hidden="true">
        <span className="workspace-rfqs-col-thumb" />
        <span className="workspace-rfqs-col-quote">Quote</span>
        <span className="workspace-rfqs-col-process">Process</span>
        <span className="workspace-rfqs-col-status">Status</span>
        <span className="workspace-rfqs-col-submitted">Submitted</span>
      </div>
      <ul className="workspace-rfqs-list">
        {items.map((rfq) => {
          const chip = RFQ_STATUS_CHIP[rfq.status]
          const lines = rfq.lines as readonly RfqLineView[] | undefined
          const firstLine = lines?.[0]
          const name = firstLine ? displayNameFor(firstLine.sourceable) : 'RFQ'
          const process = firstLine ? processLabelFor(firstLine) : '—'
          const pn = firstLine ? pnFor(firstLine, name) : undefined
          const qtyLabel = firstLine ? `Qty ${formatQuantity(firstLine.quantity)}` : ''
          // Drop the ` · pn` segment entirely rather than render a redundant
          // (or empty `'—'`) placeholder — see `pnFor`.
          const caption = [rfq.id, pn, qtyLabel].filter(Boolean).join(' · ')

          return (
            <li key={rfq.id}>
              <button type="button" className="workspace-rfq-row" onClick={() => onOpen(rfq.id)}>
                <PartThumbnail
                  className="workspace-rfq-thumb"
                  url={firstLine?.part?.thumbnailRef ?? null}
                />
                <span className="workspace-rfq-quote">
                  <span className="workspace-rfq-name">{name}</span>
                  <span className="caption-text">{caption}</span>
                </span>
                <span className="workspace-rfq-process">{process}</span>
                <span className="workspace-rfq-status">
                  <Chip tone={chip.tone}>{chip.label}</Chip>
                </span>
                <span className="workspace-rfq-submitted">
                  {rfq.createdAt ? formatDate(rfq.createdAt) : '—'}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
